import { Plus, Trash2 } from 'lucide-react';
import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import ChampIdentifiant from './ChampIdentifiant';
import ReglageBroker from './ReglageBroker';

/**
 * Les quatre équipements dont la saisie tient en quelques champs : batterie, ballon,
 * pompe à chaleur, radiateurs.
 *
 * Deux choix ont des conséquences bien au-delà de cet écran, et méritent d'être dits.
 *
 * La **technologie** du ballon décide du profil de flexibilité : `survoltage.py` classe
 * un ballon résistif et un ballon thermodynamique dans deux profils différents. Le
 * radiateur, LUI, n'a plus ce champ — voir plus bas.
 *
 * Le **contrat OA** d'une batterie signale une obligation d'achat en cours. Il change ce
 * qu'on a le droit de piloter, et se coche à la pose parce que c'est le seul moment où
 * quelqu'un a le contrat sous les yeux.
 *
 * Depuis le 22/08/2026, l'identifiant de chaque machine passe par `ChampIdentifiant` :
 * il est confronté au matériel réel, et c'est lui qui remplit la marque et le modèle.
 * Les champs de saisie restent modifiables — un poseur peut avoir une raison de
 * corriger — mais plus personne ne les invente à partir de rien.
 *
 * CE QUE CET ÉCRAN NE DIT PLUS, DEPUIS LE 28/08/2026 (retour de David)
 *
 * Les phrases d'aide décrivaient NOTRE mécanique à un poseur, parfois sous les yeux du
 * client : « c'est lui qui nomme son compte sur notre broker », « pour que
 * l'installation soit supervisée », « cela change ce qui peut être piloté », « le
 * thermostat ou le module qui pilote les radiateurs ». Elles sont retirées. Ce qui
 * reste est ce que le poseur doit FAIRE — où regarder, comment recopier — jamais ce que
 * la plateforme fait de sa saisie.
 *
 * Ne les remettez pas. Si un champ vous paraît obscur sans son explication, l'obscurité
 * est dans le LIBELLÉ du champ : c'est lui qu'il faut corriger.
 *
 * Les commentaires, eux, gardent le détail : c'est ici qu'on l'écrit, pas à l'écran.
 */

const Champ = ({ type, cle, valeur, upE, t, libelle, secours, requis, entree, min, step }) => (
  <div>
    <Label className="text-[11px] text-neutral-500">
      {t(libelle, secours)} {requis ? '*' : t('lbl_optional', '(optionnel)')}
    </Label>
    <Input
      className="h-11 text-[14px]"
      type={entree || 'text'}
      min={min}
      step={step}
      spellCheck={entree ? undefined : 'true'}
      value={valeur}
      onChange={(e) => upE(type, cle, e.target.value)}
      data-testid={`${type.toLowerCase()}-${cle.replace(/_/g, '-')}`}
    />
  </div>
);

const ChoixTechnologie = ({ type, v, upE, t, options }) => (
  <div className="space-y-2">
    <Label className="text-[11px] text-neutral-500">{t('lbl_type_ballon', 'Technologie')} *</Label>
    <div className="grid grid-cols-2 gap-2">
      {options.map((o) => (
        <button
          key={o.cle}
          type="button"
          onClick={() => upE(type, 'technologie', o.cle)}
          className={`p-3 rounded-lg border text-left transition-colors ${
            v.technologie === o.cle
              ? 'border-cyan-400 bg-cyan-50 text-neutral-900'
              : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'
          }`}
          data-testid={`${type.toLowerCase()}-techno-${o.cle}`}
        >
          <p className="text-[12.5px] font-medium">{o.label}</p>
          <p className="text-[10.5px] text-neutral-500 mt-0.5">{o.desc}</p>
        </button>
      ))}
    </div>
  </div>
);

export function BlocBatterie({ v, upE, t }) {
  const c = (cle, libelle, secours, opts = {}) => (
    <Champ type="Batterie" cle={cle} valeur={v[cle]} upE={upE} t={t}
      libelle={libelle} secours={secours} {...opts} />
  );
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        {c('puissance_kw', 'lbl_puissance_kw', 'Capacité (kWh)', { requis: true, entree: 'number', step: '0.1' })}
        {c('marque', 'lbl_marque', 'Marque', { requis: true })}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {c('modele', 'lbl_modele', 'Modèle', { requis: true })}
        {c('reference', 'lbl_reference', 'Référence', { requis: true })}
      </div>
      {/* Le numéro de série est saisi AVANT le réglage : c'est lui qui nomme le compte
          broker de cette batterie, et il figure sur l'étiquette, sous les yeux du poseur.
          L'aide affichée ne le dit plus — un poseur n'a pas à connaître notre broker.
          Depuis le 26/10 (lot L3, Loom de David du 13/10), elle dit seulement OÙ lire le
          numéro : « ce n'est pas un identifiant Tuya » égarait un poseur qui ne connaît pas
          Tuya. La consigne « caractère par caractère » n'est pas perdue : `ChampIdentifiant`
          la rappelle quand l'appareil ne répond pas, au moment où elle sert. Un seul
          chiffre de travers rend la télémétrie muette pour toujours. */}
      <ChampIdentifiant
        type="Batterie" v={v} upE={upE} t={t}
        libelle="Numéro de série de la batterie"
        aide="Le numéro de série se trouve sur l'étiquette de la batterie."
      />
      <ReglageBroker serial={v.identifiant_equipement} />
      {c('date_installation', 'lbl_date_installation', "Date d'installation", { requis: true, entree: 'date' })}
      {/* LA CASE LA PLUS CHÈRE DE L'ÉCRAN, et celle qui a demandé le plus de soin.
          Elle fait varier le revenu d'un FACTEUR 2,3 : `survoltage.classer_profil` range
          la même installation dans `bess_pv_oa` ou dans `bess_pv_non_oa` selon ce seul
          clic. David demande de retirer « cela change ce qui peut être piloté » — une
          phrase sur notre pilotage. La retirer en laissant la case nue ferait cocher
          sans savoir. L'intitulé porte donc l'information à sa place, en langage de
          client : le sigle est développé, et la ligne dit QUAND cocher. */}
      <label className="flex items-start gap-2 rounded-md border border-violet-200 bg-violet-50/40 p-3 cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={!!v.contrat_oa}
          onChange={(e) => upE('Batterie', 'contrat_oa', e.target.checked)}
          data-testid="batterie-contrat-oa"
        />
        <span>
          <span className="text-[12.5px] font-medium text-violet-800 block">Contrat d&apos;obligation d&apos;achat (OA)</span>
          <span className="text-[10.5px] text-neutral-500">
            À cocher si le client revend sa production à un tarif garanti.
          </span>
        </span>
      </label>
    </>
  );
}

export function BlocBallon({ v, upE, t }) {
  const c = (cle, libelle, secours, opts = {}) => (
    <Champ type="Ballon" cle={cle} valeur={v[cle]} upE={upE} t={t}
      libelle={libelle} secours={secours} {...opts} />
  );
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        {c('nombre_ballons', 'lbl_nombre_ballons', 'Nombre de ballons', { requis: true, entree: 'number', min: '1' })}
        {c('marque', 'lbl_marque', 'Marque', { requis: true })}
      </div>
      {c('reference', 'lbl_reference', 'Référence', { requis: true })}
      {/* L'identifiant attendu est celui du THERMOSTAT ou du RELAIS, pas la plaque du
          ballon : c'est le module que Tuya connaît. L'aide qui le disait est retirée
          (elle décrivait notre montage) ; le libellé porte désormais l'information.
          Il ne dit plus « Tuya » depuis le 26/10 (lot L3, David 13/10) : le poseur ne
          connaît pas ce nom, il lui suffit de savoir quel appareil relever. */}
      <ChampIdentifiant
        type="Ballon" v={v} upE={upE} t={t}
        libelle="ID de l'appareil pilotable"
      />
      <ChoixTechnologie
        type="Ballon" v={v} upE={upE} t={t}
        options={[
          { cle: 'resistif', label: 'Résistif', desc: 'Résistance électrique classique' },
          { cle: 'thermodynamique', label: 'Thermodynamique', desc: 'Ballon à pompe à chaleur' },
        ]}
      />
      {c('date_installation', 'lbl_date_installation', "Date d'installation", { requis: true, entree: 'date' })}
    </>
  );
}

export function BlocPAC({ v, upE, t }) {
  const c = (cle, libelle, secours, opts = {}) => (
    <Champ type="PAC" cle={cle} valeur={v[cle]} upE={upE} t={t}
      libelle={libelle} secours={secours} {...opts} />
  );
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        {c('puissance_kw', 'lbl_puissance_kw', 'Puissance (kW)', { requis: true, entree: 'number', step: '0.1' })}
        {c('marque', 'lbl_marque', 'Marque', { requis: true })}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {c('modele', 'lbl_modele', 'Modèle', { requis: true })}
        {c('reference', 'lbl_reference', 'Référence', { requis: true })}
      </div>
      {/* L'identifiant qui compte pour une pompe à chaleur est celui de son THERMOSTAT :
          c'est lui que Tuya connaît, lui que la veille interroge (`tuya_watch`), et lui
          que le pilotage écrit (`pilotage._execute_tuya`). Ce champ portait jusqu'ici le
          numéro de série constructeur de la PAC, introuvable chez Tuya par construction :
          un actif enrôlé par ce parcours ne pouvait donc structurellement pas répondre.
          Les plaques constructeur, elles, gardent leurs propres champs ci-dessous.
          L'aide « c'est par lui que la PAC est pilotée » est retirée : le libellé dit
          déjà quel appareil relever, et le reste ne regarde pas le poseur. Pour la même
          raison, il dit « ID du thermostat » et plus « ID Tuya » depuis le 26/10 (lot L3,
          David 13/10). */}
      <ChampIdentifiant
        type="PAC" v={v} upE={upE} t={t}
        libelle="ID du thermostat"
      />
      {/* Une PAC split porte deux etiquettes distinctes. Les confondre en un seul champ
          obligeait le SAV a deviner laquelle avait ete saisie. */}
      <div className="grid grid-cols-2 gap-3">
        {c('numero_serie_interieur', 'lbl_serie_pac_int', 'Numéro de série unité intérieure')}
        {c('numero_serie_exterieur', 'lbl_serie_pac_ext', 'Numéro de série unité extérieure')}
      </div>
      <div className="rounded-md border border-orange-200 bg-orange-50/40 p-3 space-y-2">
        <p className="text-[11px] font-medium text-orange-700">Thermostat</p>
        {c('thermostat_reference', 'lbl_thermostat_ref', 'Référence thermostat')}
      </div>
      {c('date_installation', 'lbl_date_installation', "Date d'installation", { requis: true, entree: 'date' })}
    </>
  );
}

/**
 * UN RADIATEUR SAISI = UN RADIATEUR POSÉ, depuis le 28/08/2026.
 *
 * L'écran ne laissait saisir qu'un seul jeu de champs pour « les radiateurs », avec un
 * compteur « Nombre de radiateurs » et UN seul identifiant Tuya. Une maison en porte
 * plusieurs, chacun avec son propre module de pilotage donc son propre identifiant :
 * le parc réel n'était pas représentable, donc ni pilotable ni valorisable. Trois
 * radiateurs posés produisent désormais TROIS actifs, chacun avec son identifiant, son
 * verdict de vérification et sa photo d'étiquette.
 *
 * Le compteur « Nombre de radiateurs » a disparu de la saisie : le nombre est celui des
 * appareils saisis. Le champ `nombre_radiateurs` reste envoyé au serveur, à 1 par actif,
 * pour que l'export distributeur et la fiche SAV continuent de le lire.
 *
 * LE CHAMP « TECHNOLOGIE » A ÉTÉ SUPPRIMÉ, et c'est une correction de fond signalée par
 * David. Ses quatre choix étaient *Standard*, *Radiateur électrique pilotable*,
 * *R2 (haute inertie)*, *Radiateur haute inertie pilotable*. Or **R2 n'est pas une
 * technologie de radiateur, c'est un mécanisme de rémunération** — la réserve
 * secondaire. Le champ mélangeait deux natures et aurait faussé un calcul de revenu.
 * R2 reste géré là où il a un sens : dans le barème de l'agrégateur
 * (`services/revenus.MECANISMES`). ⚠️ `services/survoltage.classer_profil` lit ENCORE
 * `technologie == 'r2'` sur un actif Radiateur : ce chemin n'est plus alimenté par ce
 * parcours, et c'est au lot F (agrégateurs et revenus) de décider ce qu'il devient.
 */
export const UNITE_RADIATEUR_INIT = {
  marque: '', reference: '', identifiant_equipement: '', date_installation: '',
  verification: null,
};

// L'étiquette de la photo de série d'un radiateur donné. Une par appareil : avec une
// seule photo pour trois machines, deux d'entre elles n'ont aucune preuve de pose.
export const etiquettePhotoRadiateur = (rang) => `Étiquette N° série radiateur ${rang + 1}`;

export function BlocRadiateur({ v, upE, t }) {
  const unites = v.unites || [];

  // Chaque unité est mise à jour PAR RANG, jamais en place : `ChampIdentifiant` écrit
  // `verification`, `marque` et `modele` par le même chemin que les autres blocs, et il
  // ne doit pas savoir qu'il est dans une liste. On lui passe donc un `upE` restreint à
  // son unité, de même signature.
  //
  // ⚠️ ON PART DE L'ÉTAT PRÉCÉDENT, jamais du `unites` capturé au rendu. Ces trois
  // écritures se suivent en quelques millisecondes et portent toutes la même clé : trois
  // valeurs calculées depuis le même point de départ s'écrasent, et c'est le verdict —
  // écrit en premier — qui disparaît. L'appareil répond, l'écran ne le montre pas, et
  // l'étape reste verrouillée sans que rien ne dise pourquoi.
  const majUnite = (rang, cle, valeur) => upE('Radiateur', 'unites',
    (precedentes) => (precedentes || []).map((u, i) => (i === rang ? { ...u, [cle]: valeur } : u)));

  const ajouter = () => upE('Radiateur', 'unites',
    (precedentes) => [...(precedentes || []), { ...UNITE_RADIATEUR_INIT }]);

  // Retirer une unité retire AUSSI sa photo d'étiquette et renumérote celles qui
  // suivent : sans ça, la photo du radiateur 3 resterait attachée à un rang qui
  // n'existe plus, et l'étape se verrouillerait sur une photo manquante introuvable.
  const retirer = (rang) => {
    // Le rang d'une photo se lit dans SON PROPRE libellé, jamais dans sa position dans
    // la liste : les photos sont ajoutées dans l'ordre où le poseur les prend, pas dans
    // celui des radiateurs.
    const rangDeLaPhoto = (label) => unites.findIndex((_, i) => etiquettePhotoRadiateur(i) === label);
    const photos = (v.photos_serial || [])
      .map((p) => ({ photo: p, rang: rangDeLaPhoto(p.label) }))
      .filter(({ rang: r }) => r !== rang)
      .map(({ photo, rang: r }) => (r > rang
        ? { ...photo, label: etiquettePhotoRadiateur(r - 1) }
        : photo));
    upE('Radiateur', 'unites', (precedentes) => (precedentes || []).filter((_, i) => i !== rang));
    upE('Radiateur', 'photos_serial', photos);
  };

  return (
    <>
      {unites.map((u, rang) => (
        <div
          key={rang}
          className="rounded-lg border border-rose-200 bg-rose-50/30 p-3 space-y-3"
          data-testid={`radiateur-unite-${rang}`}
        >
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-medium text-rose-700">
              {t('lbl_radiateur_rang', 'Radiateur')} {rang + 1}
            </p>
            {unites.length > 1 && (
              <Button
                type="button" variant="ghost"
                className="h-7 px-2 text-[11px] text-neutral-500 hover:text-red-600"
                onClick={() => retirer(rang)}
                data-testid={`radiateur-retirer-${rang}`}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> {t('btn_retirer', 'Retirer')}
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Champ type="Radiateur" cle={`${rang}-marque`} valeur={u.marque}
              upE={(_, __, valeur) => majUnite(rang, 'marque', valeur)}
              t={t} libelle="lbl_marque" secours="Marque" requis />
            <Champ type="Radiateur" cle={`${rang}-reference`} valeur={u.reference}
              upE={(_, __, valeur) => majUnite(rang, 'reference', valeur)}
              t={t} libelle="lbl_reference" secours="Référence" requis />
          </div>
          {/* « ID du radiateur », sans « Tuya » depuis le 26/10 (lot L3, David 13/10) :
              le poseur ne connaît pas ce nom, et le mot l'envoyait chercher ailleurs. */}
          <ChampIdentifiant
            type="Radiateur" v={u} t={t}
            upE={(_, cle, valeur) => majUnite(rang, cle, valeur)}
            tid={`radiateur-${rang}`}
            libelle="ID du radiateur"
          />
          <Champ type="Radiateur" cle={`${rang}-date-installation`} valeur={u.date_installation}
            upE={(_, __, valeur) => majUnite(rang, 'date_installation', valeur)}
            t={t} libelle="lbl_date_installation" secours="Date d'installation"
            requis entree="date" />
        </div>
      ))}
      <Button
        type="button" variant="outline" className="w-full h-11 text-[13px]"
        onClick={ajouter} data-testid="radiateur-ajouter"
      >
        <Plus className="w-4 h-4 mr-1.5" /> {t('btn_ajouter_radiateur', 'Ajouter un radiateur')}
      </Button>
    </>
  );
}
